/**
 * Notification preferences — per-category push/email toggles, optimistic
 * with revert on error. Wired: GET/PATCH /api/account/notification-prefs.
 * Port of screens-account.jsx NotifPrefs (extended per brief with a push +
 * email channel toggle per category).
 */
import React from 'react';
import { Platform, Pressable, View } from 'react-native';

import { space } from '@bero/tokens';
import { InfoNote, Toggle } from '@/components/gate/bits';
import { goBack } from '@/features/account-admin/bits';
import { Icon, type IconName } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, EmptyState, Hairline, Screen, SectionLabel, Skeleton, TopBar } from '@/ui';

type Channel = 'push' | 'email';
type Prefs = Record<string, Record<Channel, boolean>>;

// Gate-tone options (user-selectable, 2026-07-19) — files bake into build #3;
// previews stream from the web deploy so they work on every client today.
const TONES: { key: string; labelKey: DictKey; subKey: DictKey }[] = [
  { key: 'dingdong', labelKey: 'np_tone_dingdong', subKey: 'np_tone_dingdong_sub' },
  { key: 'chime', labelKey: 'np_tone_chime', subKey: 'np_tone_chime_sub' },
  { key: 'marimba', labelKey: 'np_tone_marimba', subKey: 'np_tone_marimba_sub' },
  { key: 'ring', labelKey: 'np_tone_ring', subKey: 'np_tone_ring_sub' },
];

const CATS: Array<[key: string, labelKey: DictKey, icon: IconName]> = [
  ['notices', 'np_cat_notices', 'megaphone'],
  ['visitors', 'np_cat_visitors', 'user-plus'],
  ['payments', 'np_cat_payments', 'credit-card'],
  ['events', 'np_cat_events', 'calendar'],
  ['polls', 'np_cat_polls', 'list'],
  ['community', 'np_cat_community', 'message-square'],
];

function parsePrefs(body: unknown): Prefs {
  const src = (body && typeof body === 'object'
    ? ((body as Record<string, unknown>).prefs ?? body)
    : {}) as Record<string, unknown>;
  const prefs: Prefs = {};
  for (const [key] of CATS) {
    const raw = src[key];
    if (raw && typeof raw === 'object') {
      const r = raw as Record<string, unknown>;
      prefs[key] = {
        push: typeof r.push === 'boolean' ? r.push : true,
        email: typeof r.email === 'boolean' ? r.email : true,
      };
    } else if (typeof raw === 'boolean') {
      prefs[key] = { push: raw, email: raw };
    } else {
      prefs[key] = { push: true, email: true };
    }
  }
  return prefs;
}

export default function NotificationPrefsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [prefs, setPrefs] = React.useState<Prefs | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [notice, setNotice] = React.useState<string | null>(null);
  const [gateTone, setGateTone] = React.useState('dingdong');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await api.get<unknown>('/api/account/notification-prefs');
        if (cancelled) return;
        setPrefs(parsePrefs(body));
        const savedTone = (body as { gateTone?: string })?.gateTone;
        if (typeof savedTone === 'string' && TONES.some((x) => x.key === savedTone)) setGateTone(savedTone);
        setState('ready');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const pickTone = (key: string) => {
    const prev = gateTone;
    setGateTone(key); // optimistic
    setNotice(null);
    api.patch<unknown>('/api/account/notification-prefs', { gateTone: key })
      .catch(() => {
        setGateTone(prev);
        setNotice(t('np_update_error'));
      });
  };

  const previewTone = async (key: string) => {
    const url = `https://usebero.com/sounds/gate_${key}.wav`;
    if (Platform.OS === 'web') {
      try { void new Audio(url).play(); } catch { /* autoplay blocked — ignore */ }
      return;
    }
    // Native: expo-audio bakes in build #3 — lazy + capability-checked so
    // older binaries just don't preview (selection still works).
    try {
      const Audio = await import('expo-audio');
      if (typeof Audio.createAudioPlayer !== 'function') return;
      const player = Audio.createAudioPlayer({ uri: url });
      player.play();
    } catch { /* module absent on this binary */ }
  };

  const toggle = (key: string, channel: Channel, next: boolean) => {
    if (!prefs) return;
    const prev = prefs;
    const updated: Prefs = { ...prefs, [key]: { ...prefs[key], [channel]: next } };
    setPrefs(updated); // optimistic
    setNotice(null);
    api.patch<unknown>('/api/account/notification-prefs', { category: key, channel, enabled: next, prefs: updated })
      .catch(() => {
        setPrefs(prev); // revert
        setNotice(t('np_update_error'));
      });
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('np_title')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} h={52} />)}
          </View>
        ) : state === 'error' || !prefs ? (
          <EmptyState
            icon={<Icon name="bell" size={40} color={colors.textTertiary} />}
            title={t('np_empty_title')}
            body={t('np_empty_body')}
          />
        ) : (
          <>
            <InfoNote icon="info" style={{ marginBottom: space.s4 }}>
              {t('np_always_on')}
            </InfoNote>
            {notice ? <AppText variant="bodySm" tone="danger" style={{ marginBottom: space.s3 }}>{notice}</AppText> : null}
            <SectionLabel>{t('np_categories')}</SectionLabel>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: space.s4, marginBottom: space.s1 }}>
              <AppText variant="label" tone="tertiary" style={{ width: 46, textAlign: 'center' }}>{t('np_push')}</AppText>
              <AppText variant="label" tone="tertiary" style={{ width: 46, textAlign: 'center' }}>{t('np_email')}</AppText>
            </View>
            {CATS.map(([key, labelKey, icon], i) => {
              const label = t(labelKey);
              return (
                <View key={key}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: 14 }}>
                    <Icon name={icon} size={20} color={colors.textSecondary} />
                    <AppText variant="bodyMd" style={{ flex: 1 }}>{label}</AppText>
                    <View style={{ flexDirection: 'row', gap: space.s4 }}>
                      <Toggle on={prefs[key].push} onChange={(next) => toggle(key, 'push', next)} label={t('np_push_a11y', { cat: label })} />
                      <Toggle on={prefs[key].email} onChange={(next) => toggle(key, 'email', next)} label={t('np_email_a11y', { cat: label })} />
                    </View>
                  </View>
                  {i < CATS.length - 1 ? <Hairline inset={32} /> : null}
                </View>
              );
            })}

            <SectionLabel style={{ marginTop: space.s6 }}>{t('np_gate_tone')}</SectionLabel>
            <AppText variant="bodySm" tone="tertiary" style={{ marginBottom: space.s1 }}>
              {t('np_gate_tone_sub')}
            </AppText>
            {TONES.map((tone, i) => {
              const active = gateTone === tone.key;
              const label = t(tone.labelKey);
              return (
                <View key={tone.key}>
                  <Pressable
                    accessibilityRole="radio"
                    accessibilityState={{ selected: active }}
                    accessibilityLabel={t('np_tone_a11y', { name: label })}
                    onPress={() => pickTone(tone.key)}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: 13 }}
                  >
                    <View style={{
                      width: 20, height: 20, borderRadius: 10, borderWidth: 2,
                      borderColor: active ? colors.primary500 : colors.borderFirm,
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      {active ? <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: colors.primary500 }} /> : null}
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd" weight={active ? '600' : '400'}>{label}</AppText>
                      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 1 }}>{t(tone.subKey)}</AppText>
                    </View>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={t('np_play_a11y', { name: label })}
                      onPress={() => void previewTone(tone.key)}
                      hitSlop={10}
                      style={{ paddingHorizontal: space.s2, paddingVertical: 4 }}
                    >
                      <AppText variant="bodySm" weight="600" tone="brand">{t('np_play')}</AppText>
                    </Pressable>
                  </Pressable>
                  {i < TONES.length - 1 ? <Hairline inset={32} /> : null}
                </View>
              );
            })}
          </>
        )}
      </Screen>
    </Screen>
  );
}
